import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import QSidebar from './QSidebar';
import EDD from '../assets/EDD-lgt.svg';
import CollectionDate from '../assets/Box-lgt.svg';
import Warranty from '../assets/warranty-lgt.svg';
import { QuoteContext } from '../assets/context/QuoteContext';
import { useNavigate } from 'react-router-dom';

import validator from 'validator';
import DraftSaveControl from './DraftSaveControl';
import ShipmentProtection from './ShipmentProtection';

const SRDetails = () => {
    const navigate = useNavigate();

    const { selectedQuote, quoteRequestBody, SRDetails, formData, saveSRDetails } = useContext(QuoteContext);

    const [showSenderForm, setShowSenderForm] = useState(true);
    const [senderFormData, setSenderFormData] = useState(() => SRDetails?.senderFormData || {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address1: '',
        address2: '',
        pickupInstructions: '',
    });
    const [receiverFormData, setReceiverFormData] = useState(() => SRDetails?.receiverFormData || {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address1: '',
        address2: '',
        pickupInstructions: '',
    });
    const [senderErrors, setSenderErrors] = useState({});
    const [receiverErrors, setReceiverErrors] = useState({});
    const [insuranceContinueError, setInsuranceContinueError] = useState('');
    const insuranceChoiceRef = useRef(SRDetails?.insurance || null);
    const insuranceReadyRef = useRef(!SRDetails?.insurance);

    useEffect(() => {
        if (!selectedQuote || !quoteRequestBody) {
            navigate('/CalQuote', { replace: true });
        }
    }, [navigate, quoteRequestBody, selectedQuote]);

    const handleSenderFormChange = (e) => {
        setSenderFormData({
            ...senderFormData,
            [e.target.name]: e.target.value,
        });
        setSenderErrors((prevErrors) => ({
            ...prevErrors,
            [e.target.name]: '',
        }));
    };

    const handleReceiverFormChange = (e) => {
        setReceiverFormData({
            ...receiverFormData,
            [e.target.name]: e.target.value,
        });
        setReceiverErrors((prevErrors) => ({
            ...prevErrors,
            [e.target.name]: '',
        }));
    };

    const validateSenderForm = () => {
        const errors = {};

        if (!validator.isLength(senderFormData.firstName.trim(), { min: 1 })) {
            errors.firstName = 'First Name can\'t be empty';
        }

        if (!validator.isLength(senderFormData.lastName.trim(), { min: 1 })) {
            errors.lastName = 'Last Name can\'t be empty';
        }

        if (!validator.isEmail(senderFormData.email)) {
            errors.email = 'Please enter a valid email address';
        }

        if (!validator.isLength(senderFormData.phone.trim(), { min: 1 })) {
            errors.phone = 'Sender phone number can\'t be empty';
        }

        if (!validator.isLength(senderFormData.address1.trim(), { min: 1 })) {
            errors.address1 = 'Address Line 1 can\'t be empty';
        }

        if (!validator.isLength(senderFormData.address2.trim(), { min: 1 })) {
            errors.address2 = 'Address Line 2 can\'t be empty';
        }

        if (!validator.isLength(senderFormData.pickupInstructions.trim(), { min: 1 })) {
            errors.pickupInstructions = 'Pickup Instructions can\'t be empty';
        }

        return errors;
    };

    const validateReceiverForm = () => {
        const errors = {};

        if (!validator.isLength(receiverFormData.firstName.trim(), { min: 1 })) {
            errors.firstName = 'First Name can\'t be empty';
        }

        if (!validator.isLength(receiverFormData.lastName.trim(), { min: 1 })) {
            errors.lastName = 'Last Name can\'t be empty';
        }

        if (!validator.isEmail(receiverFormData.email)) {
            errors.email = 'Please enter a valid email address';
        }

        if (!validator.isLength(receiverFormData.phone.trim(), { min: 1 })) {
            errors.phone = 'Receiver phone number can\'t be empty';
        }

        if (!validator.isLength(receiverFormData.address1.trim(), { min: 1 })) {
            errors.address1 = 'Address Line 1 can\'t be empty';
        }

        if (!validator.isLength(receiverFormData.address2.trim(), { min: 1 })) {
            errors.address2 = 'Address Line 2 can\'t be empty';
        }

        if (!validator.isLength(receiverFormData.pickupInstructions.trim(), { min: 1 })) {
            errors.pickupInstructions = 'Pickup Instructions can\'t be empty';
        }

        return errors;
    };

    const handleNextClick = () => {
        const errors = validateSenderForm();
        setSenderErrors(errors);
        if (Object.keys(errors).length === 0) {
            setShowSenderForm(false);
        }
    };

    const handleBackClick = () => {
        setShowSenderForm(true);
    };

    const handleInsuranceChange = useCallback((insurance, isReady) => {
        insuranceChoiceRef.current = insurance;
        insuranceReadyRef.current = isReady;
        setInsuranceContinueError('');
        saveSRDetails({
            senderFormData,
            receiverFormData,
            insurance,
        });
    }, [receiverFormData, saveSRDetails, senderFormData]);

    const handleReviewAndPayClick = () => {
        const senderErrors = validateSenderForm();
        const receiverErrors = validateReceiverForm();
        setSenderErrors(senderErrors);
        setReceiverErrors(receiverErrors);
        if (!insuranceReadyRef.current) {
            setInsuranceContinueError('Please wait for a current insurance price, or correct the insurance details before continuing.');
            return;
        }
        if (Object.keys(senderErrors).length === 0 && Object.keys(receiverErrors).length === 0) {
            // Proceed with review and payment
            saveSRDetails({
                senderFormData,
                receiverFormData,
                insurance: insuranceChoiceRef.current,
            });
            navigate('/Review');

        }
    };

    if (!selectedQuote || !quoteRequestBody) return null;
    const courierWarranty = selectedQuote.warranty;
    const numericCourierWarranty = Number(String(courierWarranty ?? '').replace(/[$,]/g, ''));
    const courierWarrantySummary = courierWarranty !== null && courierWarranty !== undefined && courierWarranty !== ''
        ? Number.isFinite(numericCourierWarranty)
            ? `Courier warranty: $${numericCourierWarranty.toFixed(2)} included (subject to courier terms)`
            : `Courier warranty: ${courierWarranty} (subject to courier terms)`
        : 'Courier warranty details not provided';

    return (
        <div className="container mt-5">
            {/* <h2>Compare Quotes and Book Now</h2> */}
            <div className="row mt-4 col-rev-992">
                <div className="col-lg-8">
                    <div className="container my-3">
                        <div className="cesseninfo">
                            <div className="d-flex justify-content-around py-2 sCourierInfo">
                                <div className="d-flex align-items-center justify-content-center">
                                    <div className="edd-icon">
                                        <img src={EDD} alt="" />
                                    </div>
                                    <span>EDD {selectedQuote.estimatedDelivery}</span>
                                </div>
                                <div className="d-flex align-items-center justify-content-center">
                                    <div className="edd-icon">
                                        <img src={CollectionDate} alt="" />
                                    </div>
                                    <span>{selectedQuote.collectionDate}</span>
                                </div>
                                <div className="d-flex align-items-center justify-content-center">
                                    <div className="edd-icon">
                                        <img src={Warranty} alt="" />
                                    </div>
                                    <span>{courierWarrantySummary}</span>
                                </div>
                            </div>
                        </div>
                        <ShipmentProtection
                            quoteRequestBody={quoteRequestBody}
                            carrierServiceId={selectedQuote.carrierServiceId}
                            goodsValue={formData?.goodsValue}
                            initialInsurance={SRDetails?.insurance}
                            onInsuranceChange={handleInsuranceChange}
                            summaryOnly
                        />
                        <button
                            type="button"
                            className="btn btn-stroked mt-3"
                            onClick={() => {
                                saveSRDetails({
                                    senderFormData,
                                    receiverFormData,
                                    insurance: insuranceChoiceRef.current,
                                });
                                navigate('/CalQuote');
                            }}
                        >
                            Edit shipment &amp; insurance
                        </button>
                        <div className="sr-details-container">
                            <div className="sender-details">
                                <div className="d-flex">
                                    <div className="sender-heading">
                                        <h3>{showSenderForm ? 'Sender Details (From)' : 'Receiver Details (To)'}</h3>
                                    </div>
                                    {/* <div className="sender-type d-flex align-items-center">
                                        <div className="radio-optn-cntnr">
                                            <input type="radio" name="caution" id="individual" />
                                            <label htmlFor="individual">Individual</label>
                                        </div>
                                        <div className="radio-optn-cntnr">
                                            <input type="radio" name="caution" id="individual" />
                                            <label htmlFor="individual">Company</label>
                                        </div>
                                    </div> */}
                                </div>
                                {showSenderForm && (
                                    <div className="sender-form mt-5">
                                        {Object.values(senderErrors).map((error, index) => (
                                            <p key={index} className="mt-2 color-red">{error}</p>
                                        ))}
                                        <div className="row">
                                            <div className="col-lg-4">
                                                <input
                                                    type="text"
                                                    name="firstName"
                                                    id="firstName"
                                                    placeholder="First Name"
                                                    value={senderFormData.firstName}
                                                    onChange={handleSenderFormChange}
                                                    className={senderErrors.firstName ? 'border-red' : ''}
                                                />
                                            </div>
                                            <div className="col-lg-4">
                                                <input
                                                    type="text"
                                                    name="lastName"
                                                    id="lastName"
                                                    placeholder="Last Name"
                                                    value={senderFormData.lastName}
                                                    onChange={handleSenderFormChange}
                                                    className={senderErrors.lastName ? 'border-red' : ''}
                                                />
                                            </div>
                                            <div className="col-lg-4">
                                                <input
                                                    type="email"
                                                    name="email"
                                                    id="email"
                                                    placeholder="Email"
                                                    value={senderFormData.email}
                                                    onChange={handleSenderFormChange}
                                                    className={senderErrors.email ? 'border-red' : ''}
                                                />
                                            </div>
                                        </div>
                                        <div className="row mt-4">
                                            <div className="col-lg-4">
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    id="phone"
                                                    placeholder="Sender phone (any format)"
                                                    value={senderFormData.phone}
                                                    onChange={handleSenderFormChange}
                                                    className={senderErrors.phone ? 'border-red' : ''}
                                                />
                                            </div>
                                            <div className="col-lg-4">
                                                <input
                                                    type="text"
                                                    name="address1"
                                                    id="address1"
                                                    placeholder="Address Line 1"
                                                    value={senderFormData.address1}
                                                    onChange={handleSenderFormChange}
                                                    className={senderErrors.address1 ? 'border-red' : ''}
                                                />
                                            </div>
                                            <div className="col-lg-4">
                                                <input
                                                    type="text"
                                                    name="address2"
                                                    id="address2"
                                                    placeholder="Address Line 2"
                                                    value={senderFormData.address2}
                                                    onChange={handleSenderFormChange}
                                                    className={senderErrors.address2 ? 'border-red' : ''}
                                                />
                                            </div>
                                        </div>
                                        <div className="row mt-4">
                                            <div className="col-lg-12">
                                                <input
                                                    type="text"
                                                    name="pickupInstructions"
                                                    id="PIns"
                                                    placeholder="Pickup instructions"
                                                    value={senderFormData.pickupInstructions}
                                                    onChange={handleSenderFormChange}
                                                    className={senderErrors.pickupInstructions ? 'border-red w-100' : 'w-100'}
                                                />
                                            </div>
                                        </div>
                                        <button
                                            type="submit"
                                            className="btn btn-smth mt-5"
                                            onClick={handleNextClick}
                                        >
                                            Next Receiver Details
                                        </button>
                                    </div>
                                )}
                                {!showSenderForm && (
                                    <div className="reciever-form mt-5">
                                        {Object.values(receiverErrors).map((error, index) => (
                                            <p key={index} className="mt-2 color-red">{error}</p>
                                        ))}
                                        <div className="row">
                                            <div className="col-lg-4">
                                                <input
                                                    type="text"
                                                    name="firstName"
                                                    id="firstName"
                                                    placeholder="First Name"
                                                    value={receiverFormData.firstName}
                                                    onChange={handleReceiverFormChange}
                                                    className={receiverErrors.firstName ? 'border-red' : ''}
                                                />
                                            </div>
                                            <div className="col-lg-4">
                                                <input
                                                    type="text"
                                                    name="lastName"
                                                    id="lastName"
                                                    placeholder="Last Name"
                                                    value={receiverFormData.lastName}
                                                    onChange={handleReceiverFormChange}
                                                    className={receiverErrors.lastName ? 'border-red' : ''}
                                                />
                                            </div>
                                            <div className="col-lg-4">
                                                <input
                                                    type="email"
                                                    name="email"
                                                    id="email"
                                                    placeholder="Email"
                                                    value={receiverFormData.email}
                                                    onChange={handleReceiverFormChange}
                                                    className={receiverErrors.email ? 'border-red' : ''}
                                                />
                                            </div>
                                        </div>
                                        <div className="row mt-4">
                                            <div className="col-lg-4">
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    id="phone"
                                                    placeholder="Receiver phone (any format)"
                                                    value={receiverFormData.phone}
                                                    onChange={handleReceiverFormChange}
                                                    className={receiverErrors.phone ? 'border-red' : ''}
                                                />
                                            </div>
                                            <div className="col-lg-4">
                                                <input
                                                    type="text"
                                                    name="address1"
                                                    id="address1"
                                                    placeholder="Address Line 1"
                                                    value={receiverFormData.address1}
                                                    onChange={handleReceiverFormChange}
                                                    className={receiverErrors.address1 ? 'border-red' : ''}
                                                />
                                            </div>
                                            <div className="col-lg-4">
                                                <input
                                                    type="text"
                                                    name="address2"
                                                    id="address2"
                                                    placeholder="Address Line 2"
                                                    value={receiverFormData.address2}
                                                    onChange={handleReceiverFormChange}
                                                    className={receiverErrors.address2 ? 'border-red' : ''}
                                                />
                                            </div>
                                        </div>
                                        <div className="row mt-4">
                                            <div className="col-lg-12">
                                                <input
                                                    type="text"
                                                    name="pickupInstructions"
                                                    id="DIns"
                                                    placeholder="Pickup instructions"
                                                    value={receiverFormData.pickupInstructions}
                                                    onChange={handleReceiverFormChange}
                                                    className={receiverErrors.pickupInstructions ? 'border-red w-100' : 'w-100'}
                                                />
                                            </div>
                                        </div>
                                        {insuranceContinueError && <p className="shipment-protection-message is-error" role="alert">{insuranceContinueError}</p>}
                                        <div className="d-flex">
                                            <button
                                                style={{ marginRight: '1rem' }}
                                                type="submit"
                                                className="btn btn-stroked mt-5"
                                                onClick={handleBackClick}
                                            >
                                                Back
                                            </button>
                                            <button type="submit" className="btn btn-smth mt-5" onClick={handleReviewAndPayClick}>
                                                Review & Pay
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <DraftSaveControl
                                    currentStep="details"
                                    overrides={{
                                        formData,
                                        srDetails: { senderFormData, receiverFormData, insurance: insuranceChoiceRef.current }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <QSidebar />
                </div>
            </div>
        </div>
    );
};

export default SRDetails;